import React, { Fragment, useRef, useState } from "react";
import { signup } from "../api/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import { useStateContext } from "../contexts/ContextProvider";
import { useGenders } from "../hooks/useGenders";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { trackLead } from "../utils/fbPixel";

const SignupPage = () => {
    const [errors, setErrors] = useState(null);
    const [selectedGender, setSelectedGender] = useState(null);
    const [password, setPassword] = useState("");
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const emailRef = useRef(null);
    const passwordConfirmationRef = useRef(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [passwordValidations, setPasswordValidations] = useState({
        length: false,
        letter: false,
        mixedCase: false,
        number: false,
    });
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser, setToken } = useStateContext();
    const { genders } = useGenders(); // Get genders from the hook

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);

        setPasswordValidations({
            length: value.length >= 8,
            letter: /[a-zA-Z]/.test(value),
            mixedCase: /[a-z]/.test(value) && /[A-Z]/.test(value),
            number: /\d/.test(value),
        });
    };

    const handleSignup = async (e) => {
        e.preventDefault();

        if (!termsAccepted || !privacyAccepted) {
            setErrors({ general: ["Bitte akzeptiere die Bedingungen."] }); // Use an array

            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const firstName = firstNameRef.current.value;
        const lastName = lastNameRef.current.value;
        const email = emailRef.current.value;
        const passwordConfirmation = passwordConfirmationRef.current.value;

        if (!firstName || !lastName || !email || !password || !passwordConfirmation || !selectedGender) {
            setErrors({
                firstName: ["Vorname ist erforderlich."],
                lastName: ["Nachname ist erforderlich."],
                email: ["E-Mail ist erforderlich."],
                password: ["Passwort ist erforderlich."],
                passwordConfirmation: ["Passwortbestätigung ist erforderlich."],
                gender: ["Geschlecht ist erforderlich."],
            });

            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        signup(
            firstName,
            lastName,
            email,
            password,
            passwordConfirmation,
            selectedGender // Include gender in API request
        )
            .then((data) => {
                setUser({
                    firstName: data.user.firstName,
                    lastName: data.user.lastName,
                    email: data.user.email,
                });

                setToken(data.token);

                // Track successful signup as Lead event
                trackLead();

                const from = location.state?.from || "/";
                navigate(from);
            })
            .catch((err) => {
                const response = JSON.parse(err.message);

                if (response && response.status === 422) {
                    setErrors(response.data.errors); // This will handle detailed errors
                } else {
                    setErrors({
                        general: ["An unknown error occurred. Please try again later."],
                    });
                }

                window.scrollTo({ top: 0, behavior: "smooth" });
            });
    };

    return (
        <Fragment>
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center">
                <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-6">
                    {/* Zurück Link */}
                    <div className="mb-4">
                        <button
                            className="text-pegasusPurple hover:text-pink-700 font-medium"
                            onClick={() => navigate(-1)}
                        >
                            &lt; Zurück
                        </button>
                    </div>

                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Sign up</h2>
                        <p className="text-gray-600 mb-6">Fülle die Felder aus, um ein Konto zu erstellen</p>
                    </div>

                    {/* Display Errors */}
                    {errors && (
                        <div className="text-red-500 mb-4">
                            {Object.entries(errors).map(([field, messages]) =>
                                Array.isArray(messages) // Check if messages is an array
                                    ? messages.map((message, idx) => <p key={`${field}-${idx}`}>{message}</p>)
                                    : <p key={field}>{messages}</p>
                            )}
                        </div>
                    )}

                    <form onSubmit={handleSignup}>
                        <div className="mb-4">
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                Vorname
                            </label>
                            <input
                                type="text"
                                id="firstName"
                                ref={firstNameRef}
                                className="w-full border border-gray-300 rounded-md p-2 mt-1 focus:outline-none focus:ring-pegasusPurple focus:border-pegasusPurple"
                                placeholder="Dein Vorname"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                Nachname
                            </label>
                            <input
                                type="text"
                                id="lastName"
                                ref={lastNameRef}
                                className="w-full border border-gray-300 rounded-md p-2 mt-1 focus:outline-none focus:ring-pegasusPurple focus:border-pegasusPurple"
                                placeholder="Dein Nachname"
                                required
                            />
                        </div>

                        {/* Gender Selector */}
                        <div className="mb-4">
                            <p className="block text-sm font-medium text-gray-700 mb-1">Geschlecht*</p>
                            <div className="flex space-x-4">
                                {genders.map((gender) => (
                                    <label key={gender.genderId} className="flex items-center space-x-2">
                                        <input
                                            type="radio"
                                            name="gender"
                                            value={gender.name}
                                            onChange={() => setSelectedGender(gender.name)}
                                            className="form-radio text-pegasusPurple focus:ring-pegasusPurple"
                                        />
                                        <span>{gender.displayName}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                ref={emailRef}
                                className="w-full border border-gray-300 rounded-md p-2 mt-1 focus:outline-none focus:ring-pegasusPurple focus:border-pegasusPurple"
                                placeholder="Deine Email-Adresse"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Passwort
                            </label>
                            <div className="relative">
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={handlePasswordChange}
                                    className="w-full border border-gray-300 rounded-md p-2 mt-1 focus:outline-none focus:ring-pegasusPurple focus:border-pegasusPurple"
                                    placeholder="Dein Passwort"
                                    required
                                />
                            </div>
                            {/* Password Validation */}
                            <div className="mt-2 space-y-1">
                                <div className="flex items-center">
                                    <FontAwesomeIcon
                                        icon={passwordValidations.length ? faCheckCircle : faTimesCircle}
                                        className={passwordValidations.length ? "text-green-500" : "text-gray-400"}
                                    />
                                    <span className="ml-2 text-xs">Mindestens 8 Zeichen</span>
                                </div>
                                <div className="flex items-center">
                                    <FontAwesomeIcon
                                        icon={passwordValidations.letter ? faCheckCircle : faTimesCircle}
                                        className={passwordValidations.letter ? "text-green-500" : "text-gray-400"}
                                    />
                                    <span className="ml-2 text-xs">Mindestens ein Buchstabe</span>
                                </div>
                                <div className="flex items-center">
                                    <FontAwesomeIcon
                                        icon={passwordValidations.mixedCase ? faCheckCircle : faTimesCircle}
                                        className={passwordValidations.mixedCase ? "text-green-500" : "text-gray-400"}
                                    />
                                    <span className="ml-2 text-xs">Groß- und Kleinbuchstaben</span>
                                </div>
                                <div className="flex items-center">
                                    <FontAwesomeIcon
                                        icon={passwordValidations.number ? faCheckCircle : faTimesCircle}
                                        className={passwordValidations.number ? "text-green-500" : "text-gray-400"}
                                    />
                                    <span className="ml-2 text-xs">Mindestens eine Zahl</span>
                                </div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label htmlFor="passwordConfirmation" className="block text-sm font-medium text-gray-700">
                                Passwort bestätigen
                            </label>
                            <input
                                type="password"
                                id="passwordConfirmation"
                                ref={passwordConfirmationRef}
                                className="w-full border border-gray-300 rounded-md p-2 mt-1 focus:outline-none focus:ring-pegasusPurple focus:border-pegasusPurple"
                                placeholder="Passwort bestätigen"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className="flex items-start space-x-2">
                                <input
                                    type="checkbox"
                                    checked={termsAccepted}
                                    onChange={() => setTermsAccepted(!termsAccepted)}
                                    className="mt-1 focus:ring-pegasusPurple focus:border-pegasusPurple"
                                />
                                <span className="text-sm text-gray-700">
                                    Ich akzeptiere die{" "}
                                    <a href={`${process.env.REACT_APP_BASE_SITE_URL}/allgemeine-geschaeftsbedingungen`} className="text-pegasusPurple underline">
                                        AGB
                                    </a>{" "}
                                    und die{" "}
                                    <a href={`${process.env.REACT_APP_BASE_SITE_URL}/widerrufsbelehrung`} className="text-pegasusPurple underline">
                                        Widerrufsbelehrung
                                    </a>{" "}
                                    von Pegasus Pole.
                                </span>
                            </label>
                        </div>

                        <div className="mb-6">
                            <label className="flex items-start space-x-2">
                                <input
                                    type="checkbox"
                                    checked={privacyAccepted}
                                    onChange={() => setPrivacyAccepted(!privacyAccepted)}
                                    className="mt-1 focus:ring-pegasusPurple focus:border-pegasusPurple"
                                />
                                <span className="text-sm text-gray-700">
                                    Ich akzeptiere die{" "}
                                    <a href={`${process.env.REACT_APP_BASE_SITE_URL}/datenschutzerklaerung`} className="text-pegasusPurple underline">
                                        Datenschutzbestimmungen
                                    </a>{" "}
                                    von Pegasus Pole.
                                </span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-pegasusPurple text-white py-2 px-4 rounded-md hover:bg-pegasusPurpleHover"
                        >
                            REGISTRIEREN
                        </button>
                    </form>
                </div>
            </div>
        </Fragment>
    );
};

export default SignupPage;
